import React, { useEffect, useMemo, useRef } from 'react';
import message from '@web-common/components/UI/Message/SiriusMessage';
import Button from '@web-common/components/UI/Button';
import { apiHolder as api, apis, getIn18Text, util } from 'api';
import useMsgRenderCallback from '@web-common/hooks/useMsgRenderCallback';
import useDebounceForEvent from '@web-mail/hooks/useDebounceForEvent';
import style from './index.module.scss';
import { useAppDispatch, useAppSelector } from '@web-common/state/createStore';
import { actions as mailActions } from '@web-common/state/reducer/mailReducer';
import { addWaittingSaveMail, getWaittingSaveMails, remWaittingSaveMail, resetMailWithDraft, updateWaittingSaveMail, isDynamicMail, showDynamicMailDraftWarning, } from './utils';
import SiriusMessage from '@web-common/components/UI/Message/SiriusMessage';
import { WriteMailTypes } from '@web-common/state/reducer/mailTabReducer';
import useCreateCallbackForEvent from '@web-common/hooks/useCreateCallbackForEvent';
import { tabType } from '@web-common/state/reducer/mailTabReducer';
const systemApi = api.api.getSystemApi();
const trackApi = api.api.requireLogicalApi(apis.dataTrackerApiImp);
const mailApi = api.api.requireLogicalApi(apis.mailApiImpl);
const eventApi = api.api.getEventApi();
const writeTabTypes = [
    tabType.reply,
    tabType.replyAll,
    tabType.forward,
    tabType.writeCommon,
    tabType.replyWithAttach,
    tabType.replyAllWithAttach,
    tabType.forwardAsAttach,
    tabType.edit,
    tabType.editDraft,
];
const SaveDraft = (props) => {
    const { sendLoading, execAutoSaveDraft, forceSave, tooltipVisible, updateSaveTime, saveTime } = props;
    const { currentMail, mails } = useAppSelector(state => state.mailReducer);
    const { tabList } = useAppSelector(state => state.mailTabReducer);
    const tabListRef = useRef(tabList);
    tabListRef.current = tabList;
    const subAccountExpired = useMemo(() => currentMail === null || currentMail === void 0 ? void 0 : currentMail.subAccountExpired, [currentMail]);
    const attachments = useAppSelector(state => state.attachmentReducer.attachments);
    const attachmentsRef = useRef(attachments);
    attachmentsRef.current = attachments;
    const mailsRef = useRef(mails);
    mailsRef.current = mails;
    const currentMailId = useAppSelector(state => state.mailReducer.currentMail.cid);
    const curTabTyps = useAppSelector(state => { var _a; return (_a = state.mailTabReducer.currentTab) === null || _a === void 0 ? void 0 : _a.type; });
    const currentMailIdRef = useRef(currentMailId);
    currentMailIdRef.current = currentMailId;
    const currentMailRef = useRef(currentMail);
    currentMailRef.current = currentMail;
    const forbidSaveTemp = useAppSelector(state => state.mailReducer.forbidSaveTemp);
    const forbidSaveTempRef = useRef(forbidSaveTemp);
    forbidSaveTempRef.current = forbidSaveTemp;
    const { doChangeMail, doChangeMailDraftId, doModifyMailEditStatus, doUpdateWaitSave } = mailActions;
    const initSenderStr = useMemo(() => (currentMail === null || currentMail === void 0 ? void 0 : currentMail.initSenderStr) || '', [currentMailId, currentMail === null || currentMail === void 0 ? void 0 : currentMail.initSenderStr]);
    const initSenderStrRef = useRef(initSenderStr);
    initSenderStrRef.current = initSenderStr;
    const dispatch = useAppDispatch();
    const saveClickAble = useMemo(() => {
        const { mailEditStatus } = currentMail;
        if (mailEditStatus && ['delivering', 'saving', 'reUploading', 'reGenerating'].includes(mailEditStatus))
            return false;
        if (subAccountExpired)
            return false;
        if (tooltipVisible)
            return false;
        if (sendLoading)
            return false;
        return true;
    }, [currentMail, subAccountExpired, tooltipVisible, sendLoading]);
    const pauseAutoSaveDraft = () => {
        systemApi.cancelEvent('mid', 'autoSaveDraft');
    };
    useEffect(() => {
        if (execAutoSaveDraft) {
            execAutoSaveDraft && startAutoSaveDraft();
        }
        else {
            pauseAutoSaveDraft();
        }
        return () => {
            systemApi.cancelEvent('mid', 'autoSaveDraft');
        };
    }, [execAutoSaveDraft]);
    const startAutoSaveDraft = () => {
        systemApi.intervalEvent({
            seq: 1,
            eventPeriod: 'mid',
            id: 'autoSaveDraft',
            handler: e => {
                if (e.seq % 9 === 0) {
                    eventApi.sendSysEvent({
                        eventName: 'toSaveDraft',
                        eventStrData: '',
                        eventData: {
                            type: 'localAndRemote',
                            trigger: 'internal',
                            mailId: currentMailId,
                        },
                    });
                }
                else if (e.seq % 3 === 0) {
                    doSaveDraftLocal();
                }
            },
        });
    };
    const doSaveDraftRemote = async (params) => {
        var _a, _b;
        const { taregtMailCid, silent = true, scan = false } = params;
        if (!taregtMailCid)
            return;
        let targetMail = null;
        if (taregtMailCid === currentMailIdRef.current) {
            if (currentMailRef.current.taskMail || currentMailRef.current.conference) {
                targetMail = util.cloneDeep(currentMailRef.current);
                if (currentMailRef.current.taskMail && targetMail) {
                    targetMail.taskMail = currentMailRef.current.taskMail;
                }
                if (currentMailRef.current.conference && targetMail) {
                    targetMail.conference = currentMailRef.current.conference;
                }
            }
            else {
                targetMail = util.cloneDeep(currentMailRef.current);
            }
            if (forceSave) {
                const latestCont = forceSave();
                if (((_b = (_a = targetMail === null || targetMail === void 0 ? void 0 : targetMail.entry) === null || _a === void 0 ? void 0 : _a.content) === null || _b === void 0 ? void 0 : _b.content) && latestCont) {
                    targetMail.entry.content.content = latestCont;
                }
            }
        }
        else {
            targetMail = mailsRef.current.find(item => item.cid === taregtMailCid);
        }
        if (!targetMail)
            return;
        if (!(targetMail === null || targetMail === void 0 ? void 0 : targetMail.entry))
            return;
        const curCid = targetMail.cid;
        const filterAttachments = attachmentsRef.current.filter(att => String(att.mailId) === String(targetMail.cid));
        targetMail = Object.assign(Object.assign(Object.assign({}, targetMail), { entry: Object.assign(Object.assign({}, targetMail.entry), { attachment: filterAttachments, attachmentCount: filterAttachments.length }) }), { _account: targetMail.initSenderStr });
        dispatch(doModifyMailEditStatus({ cid: taregtMailCid, status: 'saving' }));
        mailApi
            .doSaveTemp({ content: targetMail, saveDraft: true, auto: silent })
            .then(res => {
            var _a;
            const { cid, _id, draftId } = res;
            if (cid) {
                if ((_a = targetMail === null || targetMail === void 0 ? void 0 : targetMail.status) === null || _a === void 0 ? void 0 : _a.puretext) {
                    dispatch(doChangeMailDraftId({ cid, draftId, _id }));
                }
                else {
                    const draftedMail = Object.assign(Object.assign({}, targetMail), { _id,
                        draftId });
                    dispatch(doChangeMail(draftedMail));
                }
            }
            !silent &&
                message.info({
                    content: getIn18Text('YICUNZHI\u201CCAO'),
                });
            if (taregtMailCid === currentMailIdRef.current) {
                updateSaveTime();
            }
        })
            .catch(err => {
            console.log('远端存储失败', err);
            if ((err === 'FA_COMPOSE_NOT_FOUND' || err === 'FA_ID_NOT_FOUND') && taregtMailCid === currentMailIdRef.current) {
                if ((targetMail === null || targetMail === void 0 ? void 0 : targetMail.initSenderStr) === systemApi.getMainAccount1().email) {
                    taregtMailCid && resetMailWithDraft(taregtMailCid);
                    return;
                }
            }
            !silent &&
                message.fail({
                    content: `保存草稿箱失败${(err === null || err === void 0 ? void 0 : err.title) ? `:${err.title}` : ''}`,
                });
        })
            .finally(() => {
            dispatch(doUpdateWaitSave({ cid: curCid, val: false }));
            dispatch(doModifyMailEditStatus({ cid: curCid, status: '' }));
            scan && silentScanOtherMails();
        });
    };
    const doSaveDraftLocal = useCreateCallbackForEvent(async () => {
        var _a, _b;
        const { mailEditStatus } = currentMailRef.current;
        if (mailEditStatus && ['saving', 'delivering', 'reUploading', 'reGenerating'].includes(mailEditStatus))
            return false;
        if (forbidSaveTempRef.current)
            return;
        if (initSenderStrRef.current !== systemApi.getMainAccount1().email)
            return;
        let data = util.cloneDeep(currentMailRef.current);
        if (forceSave) {
            const latestCont = forceSave();
            ((_b = (_a = data === null || data === void 0 ? void 0 : data.entry) === null || _a === void 0 ? void 0 : _a.content) === null || _b === void 0 ? void 0 : _b.content) && (data.entry.content.content = latestCont);
        }
        const filterAttachments = attachmentsRef.current.filter(att => String(att.mailId) === String(data.cid));
        data = Object.assign(Object.assign(Object.assign({}, data), { entry: Object.assign(Object.assign({}, data.entry), { attachment: filterAttachments, attachmentCount: filterAttachments.length }) }), { _account: initSenderStrRef.current });
        try {
            if (!currentMailRef.current.draftId && mailRemoteSaveCheck(currentMailIdRef.current)) {
                doSaveDraftRemote({ taregtMailCid: currentMailIdRef.current, silent: true, scan: false });
            }
            else {
                const saveLocalRes = await mailApi.doSaveDraftLocal(data);
                silentScanOtherMails();
                console.log('saveLocalRessaveLocalRes', saveLocalRes);
            }
        }
        catch (error) {
            console.log('定时本地存储失败', error);
        }
    });
    const silentScanOtherMails = useDebounceForEvent(() => {
        const tabArr = tabListRef.current || [];
        console.log('tabArrtabArr', tabArr);
        const writeMailActiveTabIds = tabArr
            .filter(tab => {
            const { isActive, type } = tab;
            return isActive && WriteMailTypes.includes(type);
        })
            .map(writeTab => writeTab.id);
        const unRemoteSaveMails = mailsRef.current.filter(item => {
            const { cid, draftId } = item;
            if (cid === currentMailIdRef.current)
                return false;
            if (!writeMailActiveTabIds.includes(String(cid)))
                return false;
            if (draftId)
                return false;
            const checkRes = mailRemoteSaveCheck(item.cid);
            if (!checkRes)
                return false;
            return true;
        });
        if (!(unRemoteSaveMails === null || unRemoteSaveMails === void 0 ? void 0 : unRemoteSaveMails.length))
            return;
        unRemoteSaveMails.forEach(item => {
            doSaveDraftRemote({ taregtMailCid: item.cid, silent: true, scan: false });
        });
    }, 2000);
    const execEventSaveDraft = (params) => {
        const { mailId } = params;
        const checkRes = mailRemoteSaveCheck(mailId);
        if (!checkRes)
            return;
        doSaveDraftRemote({ taregtMailCid: mailId, silent: true, scan: true });
    };
    const mailRemoteSaveCheck = (saveCid) => {
        if (!saveCid)
            return false;
        const target = mailsRef.current.find(item => item.cid === saveCid);
        if (!target)
            return false;
        const { mailEditStatus } = target;
        if (mailEditStatus && ['saving', 'delivering', 'reUploading', 'reGenerating'].includes(mailEditStatus))
            return false;
        const currentMailAttachments = attachmentsRef.current.filter(item => item.mailId === saveCid && item.type !== 'download');
        if (currentMailAttachments.some(item => (item === null || item === void 0 ? void 0 : item.status) && ['uploading', 'deleting'].includes(item === null || item === void 0 ? void 0 : item.status)))
            return false;
        return true;
    };
    const isWriteMailTab = useMemo(() => {
        return writeTabTypes.includes(curTabTyps);
    }, [curTabTyps]);
    useMsgRenderCallback('mailMenuOper', ev => {
        if (ev.eventStrData === 'saveDraft' && isWriteMailTab) {
            clickSave();
        }
    });
    const clickSave = useDebounceForEvent(e => {
        e && e.stopPropagation();
        if (!saveClickAble)
            return false;
        if (isDynamicMail(currentMailRef.current)) {
            showDynamicMailDraftWarning();
            return false;
        }
        trackApi.track('pcMail_click_saveDraft_writeMailPage');
        const currentAttachments = attachmentsRef.current.filter(item => item.mailId === currentMailIdRef.current && item.type !== 'download');
        if (currentAttachments.some(item => (item === null || item === void 0 ? void 0 : item.status) && ['uploading', 'deleting'].includes(item === null || item === void 0 ? void 0 : item.status))) {
            SiriusMessage.info(getIn18Text('ZHENGZAISHANGCHUANFUJ，SCWCHJZDBCDCGX'));
            return;
        }
        const checkRes = mailRemoteSaveCheck(currentMailId);
        if (checkRes) {
            doSaveDraftRemote({ taregtMailCid: currentMailIdRef.current, silent: false, scan: true });
        }
    }, 500);
    const manageEventSaveDraft = (ev) => {
        const { eventData } = ev;
        const curCid = eventData.mailId || currentMailIdRef.current;
        const waitItem = getWaittingSaveMails().get(curCid);
        if (waitItem) {
            const curItem = Object.assign({}, waitItem);
            const { st } = curItem;
            clearTimeout(st);
            curItem.st = setTimeout(() => {
                clearTimeout(st);
                execEventSaveDraft({ mailId: curCid });
                remWaittingSaveMail({ remId: curCid });
            }, 1000);
            console.log('updateWaittingSaveMail', curCid);
            updateWaittingSaveMail({ updateId: curCid, obj: curItem });
        }
        else {
            const st = setTimeout(() => {
                clearTimeout(st);
                execEventSaveDraft({ mailId: curCid });
                remWaittingSaveMail({ remId: curCid });
            }, 1000);
            addWaittingSaveMail({ addId: curCid, obj: { st } });
        }
    };
    useMsgRenderCallback('toSaveDraft', (ev) => {
        if (forbidSaveTempRef.current)
            return;
        manageEventSaveDraft(ev);
    });
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: `${style.marginRight8}` },
            React.createElement(Button, { id: "saveDraft", btnType: "minorGray", size: "small", inline: true, disabled: !saveClickAble, onClick: e => clickSave(e) },
                getIn18Text('CUNCAOGAO'),
                saveTime ? React.createElement("span", { className: style.saveDraftTime }, saveTime + getIn18Text('BAOCUN')) : ''))));
};
export default SaveDraft;
export const SaveDraftClone = (props) => {
    const { tooltipVisible, sendLoading } = props;
    const { currentMail } = useAppSelector(state => state.mailReducer);
    const saveClickAble = useMemo(() => {
        const { mailEditStatus, subAccountExpired } = currentMail;
        if (mailEditStatus && ['delivering', 'saving', 'reUploading', 'reGenerating'].includes(mailEditStatus))
            return false;
        if (subAccountExpired)
            return false;
        if (tooltipVisible)
            return false;
        if (sendLoading)
            return false;
        return true;
    }, [currentMail, tooltipVisible, sendLoading]);
    const ckAction = (e) => {
        var _a;
        e.stopPropagation();
        (_a = document.getElementById('saveDraft')) === null || _a === void 0 ? void 0 : _a.click();
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: `${style.marginRight8}` },
            React.createElement(Button, { btnType: "minorGray", size: "small", inline: true, disabled: !saveClickAble, onClick: e => ckAction(e) }, getIn18Text('CUNCAOGAO')))));
};
